import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpperCasePipe } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCalendar, MatCalendarView } from '@angular/material/datepicker';
import { IconUIComponent } from '../../../../atoms/icon/icon.component';

const CalendarView = {
  Month: 'month',
  Year: 'year',
  MultiYear: 'multi-year',
} as const satisfies Record<string, MatCalendarView>;

/** Years one page of Material's multi-year view shows. */
const YEARS_PER_PAGE = 24;

/** Calendar header for `mat-calendar`, moved here from the picker it is replacing. */
@Component({
  selector: 'fr8bot-ui-date-picker-calendar-header',
  imports: [UpperCasePipe, IconUIComponent],
  templateUrl: './calendar-header.component.html',
  styleUrl: './calendar-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerCalendarHeaderUIComponent {
  readonly calendar = inject<MatCalendar<Date>>(MatCalendar);
  private readonly dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly dateFormats = inject(MAT_DATE_FORMATS);

  /**
   * Built from the `DateAdapter` rather than Angular's `DatePipe`. The pipe formats with
   * `LOCALE_ID` — the browser's tag — so this header read `AUG 2026` above a calendar
   * whose own grid said `серп.`. The adapter carries the locale the picker was given.
   */
  protected get periodLabel(): string {
    const { currentView, activeDate } = this.calendar;

    if (currentView === CalendarView.Month) {
      return this.dateAdapter.format(activeDate, this.dateFormats.display.monthYearLabel);
    }

    if (currentView === CalendarView.Year) {
      return this.dateAdapter.getMonthNames('short')[this.dateAdapter.getMonth(activeDate)]!;
    }

    return this.dateAdapter.getYearName(activeDate);
  }

  constructor() {
    // `currentView` and `activeDate` are plain properties on someone else's component, so
    // nothing marks this one dirty when the calendar changes them from the inside — picking
    // a year in the multi-year grid left the header rendering the view and date it opened
    // with. `stateChanges` is the notification Material provides for exactly this, and its
    // own `MatCalendarHeader` subscribes to it the same way.
    const changeDetectorRef = inject(ChangeDetectorRef);
    this.calendar.stateChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => changeDetectorRef.markForCheck());
  }

  switchView(): void {
    this.calendar.currentView =
      this.calendar.currentView === CalendarView.Month
        ? CalendarView.MultiYear
        : CalendarView.Month;
  }

  moveDate(amount: number): void {
    const { currentView, activeDate } = this.calendar;

    if (currentView === CalendarView.Month) {
      this.calendar.activeDate = this.dateAdapter.addCalendarMonths(activeDate, amount);
      return;
    }

    this.calendar.activeDate = this.dateAdapter.addCalendarYears(
      activeDate,
      currentView === CalendarView.MultiYear ? amount * YEARS_PER_PAGE : amount,
    );
  }
}
